// DDoS Active Blocks — admin-only page that lists and clears Redis block
// keys (ddos:block:* / ddos:fpblock:*) written by the AD engine. Distinct
// from /ddos/exceptions which only adds pre-emptive allows.
//
// API:  GET    /api/admin/ad/blocks?domain=&ip=&uid=&fp=&limit=
//       POST   /api/admin/ad/blocks/unblock  body = {domain,ip,uid,fp,dry_run} OR {keys:[]}
//       GET    /api/admin/ad/blocks/health
import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";

import adminAxios from "../../../utils/axios/v1/adminAxios";
import { IOSSwitch, SnackbarAlert } from "../../../components/pages/common/styled";

import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
  StatTile,
  StatusPill,
  EmptyState,
  ConfirmModal,
} from "../../../components/pages/_new/super/design";

const thCellSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdCellSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  color: TOKENS.fg1,
  fontSize: 13,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${TOKENS.radiusXs}px`,
    background: TOKENS.bgSurface,
  },
};

function reasonStatus(r) {
  switch (r) {
    case "triple":
      return "Expired";
    case "domain_ip":
    case "domain_uid":
      return "Suspended";
    case "domain":
      return "Trial";
    case "fingerprint":
      return "Pending";
    default:
      return "Pending";
  }
}

export default function DdosBlocks() {
  const [domain, setDomain] = React.useState("");
  const [ip, setIp] = React.useState("");
  const [uid, setUid] = React.useState("");
  const [fp, setFp] = React.useState("");
  const [limit, setLimit] = React.useState(500);
  const [rows, setRows] = React.useState(null);
  const [capped, setCapped] = React.useState(false);
  const [selected, setSelected] = React.useState(new Set());
  const [dryRun, setDryRun] = React.useState(false);
  const [health, setHealth] = React.useState(null);

  // confirm modals
  const [confirmSelectedOpen, setConfirmSelectedOpen] = React.useState(false);
  const [confirmSpecOpen, setConfirmSpecOpen] = React.useState(false);
  const [confirmLoading, setConfirmLoading] = React.useState(false);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMsg, setSnackMsg] = React.useState("");
  const [snackSev, setSnackSev] = React.useState("info");

  const search = async () => {
    setRows(null);
    setSelected(new Set());
    try {
      const params = {};
      if (domain) params.domain = domain;
      if (ip) params.ip = ip;
      if (uid) params.uid = uid;
      if (fp) params.fp = fp;
      params.limit = limit;
      const r = await adminAxios.get(`/ad/blocks`, { params });
      setRows(r.data.items || []);
      setCapped(!!r.data.capped);
    } catch (e) {
      setSnackSev("error");
      setSnackMsg(e?.message || String(e));
      setSnackOpen(true);
      setRows([]);
    }
  };

  const probeHealth = async () => {
    try {
      const r = await adminAxios.get(`/ad/blocks/health`);
      setHealth(r.data);
    } catch (e) {
      setHealth({ ok: false, error: e?.message || String(e) });
    }
  };

  React.useEffect(() => {
    probeHealth();
  }, []);

  const toggleRow = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };
  const selectAll = () => {
    if (!rows) return;
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.key)));
  };

  const doUnblockSelected = async () => {
    setConfirmLoading(true);
    try {
      const body = { keys: [...selected], dry_run: dryRun };
      const r = await adminAxios.post(`/ad/blocks/unblock`, body);
      setSnackSev("success");
      setSnackMsg(dryRun ? `Dry-run: would delete ${r.data.would_delete}` : `Deleted ${r.data.deleted} of ${selected.size}`);
      setSnackOpen(true);
      if (!dryRun) search();
    } catch (e) {
      setSnackSev("error");
      setSnackMsg(e?.message || String(e));
      setSnackOpen(true);
    }
    setConfirmLoading(false);
    setConfirmSelectedOpen(false);
  };
  const unblockSelected = () => {
    if (selected.size === 0) {
      setSnackSev("warning");
      setSnackMsg("Select at least one row");
      setSnackOpen(true);
      return;
    }
    if (dryRun) {
      doUnblockSelected();
    } else {
      setConfirmSelectedOpen(true);
    }
  };

  const doUnblockBySpec = async () => {
    setConfirmLoading(true);
    try {
      const spec = { domain, ip, uid, fp, dry_run: dryRun };
      const r = await adminAxios.post(`/ad/blocks/unblock`, spec);
      setSnackSev("success");
      setSnackMsg(dryRun ? `Dry-run: would delete ${r.data.would_delete}` : `Deleted ${r.data.deleted}`);
      setSnackOpen(true);
      if (!dryRun) search();
    } catch (e) {
      setSnackSev("error");
      setSnackMsg(e?.message || String(e));
      setSnackOpen(true);
    }
    setConfirmLoading(false);
    setConfirmSpecOpen(false);
  };
  const unblockBySpec = () => {
    if (!domain && !fp) {
      setSnackSev("warning");
      setSnackMsg("Need at least domain or fp");
      setSnackOpen(true);
      return;
    }
    if (dryRun) doUnblockBySpec();
    else setConfirmSpecOpen(true);
  };

  // Counts for stat tiles
  const counts = React.useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const uniqueIps = new Set(list.map((r) => r.ip).filter(Boolean)).size;
    const expiringSoon = list.filter((r) => typeof r.ttl_seconds === "number" && r.ttl_seconds > 0 && r.ttl_seconds <= 60).length;
    return { total: list.length, uniqueIps, expiringSoon };
  }, [rows]);

  const isLoading = rows === null;

  return (
    <React.Fragment>
      <Helmet title="DDoS Active Blocks" />
      <PageHeader
        title="DDoS Active Blocks"
        description="Active Redis block keys written by the AD engine. Clears propagate to every Layerd node."
        actions={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {health && <StatusPill status={health.ok ? "Active" : "Expired"} />}
            <OutlinedButton onClick={probeHealth} startIcon={<RefreshIcon style={{ width: 18, height: 18 }} />}>
              Re-check
            </OutlinedButton>
          </Stack>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatTile icon={<BlockIcon />} value={isLoading ? "—" : counts.total} label="Active blocks" sub={capped ? "Capped at limit — refine filters" : undefined} />
        <StatTile
          icon={<ShieldOutlinedIcon />}
          iconBg={TOKENS.infoBg}
          iconInk={TOKENS.infoInk}
          value={isLoading ? "—" : counts.uniqueIps}
          label="Unique IPs"
        />
        <StatTile
          icon={<RefreshIcon />}
          iconBg={TOKENS.warningBg}
          iconInk={TOKENS.warningInk}
          value={isLoading ? "—" : counts.expiringSoon}
          label="Expiring < 60s"
        />
      </Box>

      <Stack spacing={2}>
        <SectionCard title="Filters" description="Narrow Redis block search by any combination">
          <Grid container spacing={1.5} alignItems="flex-end">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="IP"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                placeholder="1.2.3.4"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="UID"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="abc123"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Fingerprint"
                value={fp}
                onChange={(e) => setFp(e.target.value)}
                placeholder="ja4 hash"
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Limit"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10) || 500)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <PrimaryButton
                onClick={search}
                startIcon={<SearchIcon style={{ width: 18, height: 18 }} />}
                sx={{ width: "100%" }}
              >
                Search
              </PrimaryButton>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <FormControlLabel
              control={<IOSSwitch checked={dryRun} onChange={() => setDryRun(!dryRun)} />}
              label={<Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>Dry run</Typography>}
            />
            <OutlinedButton
              startIcon={<LockOpenIcon style={{ width: 18, height: 18 }} />}
              onClick={unblockSelected}
              disabled={!rows || selected.size === 0}
            >
              Unblock selected ({selected.size})
            </OutlinedButton>
            <OutlinedButton
              startIcon={<LockOpenIcon style={{ width: 18, height: 18 }} />}
              onClick={unblockBySpec}
              sx={{ borderColor: TOKENS.warning, color: TOKENS.warningInk }}
            >
              Unblock by spec (filters → 1 key)
            </OutlinedButton>
          </Box>
        </SectionCard>

        <Box
          sx={{
            border: `1px solid ${TOKENS.border}`,
            borderRadius: `${TOKENS.radiusM}px`,
            background: TOKENS.bgSurface,
            boxShadow: TOKENS.shadowXs,
            overflow: "hidden",
          }}
        >
          {isLoading ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress size={28} sx={{ color: TOKENS.primary }} />
            </Stack>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<BlockIcon />}
              title="No active blocks match"
              body="Adjust your filters or hit Search again."
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={thCellSx}>
                      <Checkbox
                        size="small"
                        checked={selected.size === rows.length && rows.length > 0}
                        indeterminate={selected.size > 0 && selected.size < rows.length}
                        onChange={selectAll}
                      />
                    </TableCell>
                    <TableCell sx={thCellSx}>Reason</TableCell>
                    <TableCell sx={thCellSx}>Key</TableCell>
                    <TableCell sx={thCellSx}>Domain</TableCell>
                    <TableCell sx={thCellSx}>IP</TableCell>
                    <TableCell sx={thCellSx}>UID</TableCell>
                    <TableCell sx={thCellSx}>FP</TableCell>
                    <TableCell sx={thCellSx} align="right">TTL (s)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={r.key}
                      hover
                      selected={selected.has(r.key)}
                      sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                    >
                      <TableCell padding="checkbox" sx={tdCellSx}>
                        <Checkbox size="small" checked={selected.has(r.key)} onChange={() => toggleRow(r.key)} />
                      </TableCell>
                      <TableCell sx={tdCellSx}>
                        <StatusPill status={reasonStatus(r.reason)} />
                        <Typography sx={{ fontSize: 11, color: TOKENS.fg3, mt: 0.25 }}>{r.reason}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdCellSx, fontFamily: "ui-monospace, monospace", fontSize: 11, color: TOKENS.fg2 }}>
                        {r.key}
                      </TableCell>
                      <TableCell sx={tdCellSx}>{r.domain || "—"}</TableCell>
                      <TableCell sx={{ ...tdCellSx, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                        {r.ip || "—"}
                      </TableCell>
                      <TableCell sx={tdCellSx}>{r.uid || "—"}</TableCell>
                      <TableCell sx={{ ...tdCellSx, fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
                        {r.fp || "—"}
                      </TableCell>
                      <TableCell sx={tdCellSx} align="right">
                        {r.ttl_seconds ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Stack>

      <ConfirmModal
        open={confirmSelectedOpen}
        onClose={() => setConfirmSelectedOpen(false)}
        onConfirm={doUnblockSelected}
        variant="danger"
        title={`Unblock ${selected.size} key${selected.size === 1 ? "" : "s"}?`}
        body="This permanently DELs the Redis block keys. Mitigation will stop immediately."
        submitLabel="Unblock"
        loading={confirmLoading}
      />
      <ConfirmModal
        open={confirmSpecOpen}
        onClose={() => setConfirmSpecOpen(false)}
        onConfirm={doUnblockBySpec}
        variant="danger"
        title="Unblock matching key?"
        body="This permanently DELs the matching Redis block key. Mitigation will stop immediately."
        submitLabel="Unblock"
        loading={confirmLoading}
      />
      <SnackbarAlert open={snackOpen} onClose={() => setSnackOpen(false)} severity={snackSev} message={snackMsg} />
    </React.Fragment>
  );
}
